import { type SourceId } from '@/james-playground/aa-family-claim/claim/facts';

/**
 * The claim as the family meets it: areas on the hub, and the asks inside
 * them. An ask is one prompt in a round. It owns a set of registry entries,
 * and every entry the product asks about belongs to exactly one ask
 * (claim.test.ts holds that). The form's numbering stays in the registry.
 *
 * Copy that mentions her has a "you" form for a claimant using the app
 * herself; `{name}` is her first name.
 */

export type Copy = string | { she: string; you: string };

export type AreaId =
  | 'start'
  | 'about'
  | 'home'
  | 'health'
  | 'helpers'
  | 'day'
  | 'safe'
  | 'night'
  | 'stays'
  | 'payment'
  | 'signing'
  | 'stronger';

export type Area = { id: AreaId; label: Copy; care?: boolean };

/** Hub order. "start" is the Before we start screen and never on the hub. */
export const AREAS: Area[] = [
  { id: 'start', label: 'Before we start' },
  { id: 'about', label: { she: 'About {name}', you: 'About you' } },
  { id: 'home', label: 'Where she lives and time away' },
  { id: 'health', label: 'Health and treatment' },
  { id: 'helpers', label: { she: 'Who helps {name}', you: 'Who helps you' } },
  { id: 'day', label: 'Help in the day', care: true },
  {
    id: 'safe',
    label: { she: 'Keeping her safe in the day', you: 'Staying safe in the day' },
    care: true,
  },
  { id: 'night', label: 'Help at night', care: true },
  { id: 'stays', label: 'Hospital and care stays' },
  { id: 'payment', label: 'Benefits and payment' },
  { id: 'signing', label: 'Signing and sending' },
  { id: 'stronger', label: 'Make it stronger' },
];

export type AskKind =
  | 'confirm'
  | 'fields'
  | 'checks'
  | 'choice'
  | 'repeat'
  | 'helpGrid'
  | 'nightGrid'
  | 'watch'
  | 'falls'
  | 'activities'
  | 'amount'
  | 'approxDate'
  | 'stay'
  | 'bank'
  | 'signer'
  | 'consent'
  | 'documents'
  | 'quiet'
  | 'startDate'
  | 'sr1'
  | 'variability'
  | 'narrative'
  | 'supporting';

/** One activity row of a help grid: the entry behind each kind of help. */
export type GridRow = {
  label: Copy;
  difficulty?: string;
  help?: string;
  prompt?: string;
};

export type NightRow = { label: Copy; times: string; minutes: string };

export type Ask = {
  id: string;
  area: AreaId;
  kind: AskKind;
  entries: string[];
  title: Copy;
  /** What the round opens with. */
  prompt: Copy;
  /** For a grid: ticks (Yes/No) or a count each day. */
  unit?: 'tick' | 'perDay';
  rows?: GridRow[];
  nightRows?: NightRow[];
  /** The entry whose Yes opens the detail (the form's own Yes/No). */
  gate?: string;
};

/** How hard an ask is to answer, used to rank the next step. */
export const EFFORT: Record<AskKind, number> = {
  confirm: 1,
  choice: 1,
  checks: 1,
  quiet: 1,
  startDate: 1,
  consent: 1,
  documents: 1,
  sr1: 1,
  variability: 1,
  amount: 2,
  approxDate: 2,
  fields: 3,
  helpGrid: 3,
  nightGrid: 3,
  watch: 3,
  falls: 3,
  activities: 3,
  stay: 3,
  signer: 3,
  narrative: 3,
  supporting: 3,
  repeat: 4,
  bank: 4,
};

const ask = (a: Ask): Ask => a;

export const ASKS: Ask[] = [
  // ---- Before we start ---------------------------------------------------
  ask({
    id: 'start-award',
    area: 'start',
    kind: 'choice',
    entries: ['existingDisabilityAward'],
    title: 'Disability benefits now',
    prompt: {
      she: 'Does {name} get any of these now?',
      you: 'Do you get any of these now?',
    },
  }),
  ask({
    id: 'start-quiet',
    area: 'start',
    kind: 'quiet',
    entries: ['specialRules', 'talkAboutQuickerRoute'],
    title: 'One more thing',
    prompt: {
      she: 'Has a doctor or nurse said {name} may be nearing the end of her life?',
      you: 'Has a doctor or nurse said you may be nearing the end of your life?',
    },
  }),
  ask({
    id: 'start-date',
    area: 'start',
    kind: 'startDate',
    entries: ['claimFormRequestedOn'],
    title: 'Protect the start date',
    prompt:
      'Attendance Allowance starts from the day DWP gets the claim. Ring them for a claim form and it counts from the day you ring, as long as you send it within 6 weeks.',
  }),

  // ---- About her -----------------------------------------------------------
  ask({
    id: 'about-identity',
    area: 'about',
    kind: 'confirm',
    entries: ['surname', 'firstNames', 'otherNames', 'title', 'dob', 'sex'],
    title: { she: 'Her name and date of birth', you: 'Your name and date of birth' },
    prompt: 'Is this right?',
  }),
  ask({
    id: 'about-address',
    area: 'about',
    kind: 'confirm',
    entries: ['address', 'postcode'],
    title: { she: 'Where {name} lives', you: 'Where you live' },
    prompt: 'Is this her permanent address?',
  }),
  ask({
    id: 'about-ni',
    area: 'about',
    kind: 'fields',
    entries: ['niNumber'],
    title: 'National Insurance number',
    prompt: {
      she: "What's {name}'s National Insurance number? It's on letters about her pension.",
      you: "What's your National Insurance number? It's on letters about your pension.",
    },
  }),
  ask({
    id: 'about-phone',
    area: 'about',
    kind: 'fields',
    entries: ['mobile', 'phone'],
    title: 'Phone numbers',
    prompt: { she: 'How can DWP reach {name}?', you: 'How can DWP reach you?' },
  }),

  // ---- Where she lives and time away ----------------------------------
  ask({
    id: 'home-nationality',
    area: 'home',
    kind: 'choice',
    entries: ['nationality', 'eeaBefore2021'],
    title: 'Nationality',
    prompt: { she: "What's {name}'s nationality?", you: "What's your nationality?" },
  }),
  ask({
    id: 'home-residence',
    area: 'home',
    kind: 'checks',
    entries: [
      'normallyLiveGb',
      'welshContact',
      'abroadOverFourWeeks',
      'eeaBenefits',
      'eeaWorkInsurance',
      'movedFromScotland',
    ],
    title: 'Living in Great Britain',
    prompt: 'A few quick checks DWP asks everyone.',
  }),
  ask({
    id: 'home-abroad',
    area: 'home',
    kind: 'fields',
    entries: ['abroadFrom', 'abroadTo', 'abroadWhere', 'abroadReason'],
    title: 'Time abroad',
    prompt: 'When was she away, where, and why?',
  }),
  ask({
    id: 'home-scotland',
    area: 'home',
    kind: 'fields',
    entries: ['scotlandMoveDate', 'padpClaim'],
    title: 'Moving from Scotland',
    prompt: 'When did she move, and did she claim Pension Age Disability Payment there?',
  }),
  ask({
    id: 'home-living',
    area: 'home',
    kind: 'choice',
    entries: [
      'accommodation',
      'toiletLocation',
      'toiletOtherLocation',
      'sleepLocation',
      'sleepOtherLocation',
    ],
    title: { she: 'Her home', you: 'Your home' },
    prompt: {
      she: 'What sort of home does {name} live in, and where are the toilet and her bed?',
      you: 'What sort of home do you live in, and where are the toilet and your bed?',
    },
  }),
  ask({
    id: 'home-aids',
    area: 'home',
    kind: 'repeat',
    entries: ['aidsTable'],
    title: 'Aids and adaptations',
    prompt: 'Does she use anything to help her, like a frame, rails or a raised toilet seat?',
  }),

  // ---- Health and treatment -------------------------------------------
  ask({
    id: 'health-conditions',
    area: 'health',
    kind: 'repeat',
    entries: ['illnessesTable'],
    title: 'Conditions and medicines',
    prompt: 'What conditions does she have, and what does she take for each?',
  }),
  ask({
    id: 'health-gp',
    area: 'health',
    kind: 'fields',
    entries: ['gpName', 'gpAddress', 'gpPostcode', 'gpPhone', 'gpLastSeen'],
    title: 'Her GP',
    prompt: 'Which surgery is she with, and when did she last see them?',
  }),
  ask({
    id: 'health-professionals',
    area: 'health',
    kind: 'repeat',
    gate: 'seenProfessional',
    entries: ['seenProfessional', 'professionalsTable'],
    title: 'Other professionals',
    prompt:
      'In the last 12 months, has she seen anyone else about her conditions, like a consultant, nurse or physio?',
  }),
  ask({
    id: 'health-reports',
    area: 'health',
    kind: 'repeat',
    entries: [
      'hasReports',
      'surgeryWaitingList',
      'surgeryWaitingListTable',
      'hadTests',
      'testsTable',
    ],
    title: 'Reports, tests and waiting lists',
    prompt: 'Any reports, tests or operations she is waiting for?',
  }),

  // ---- Who helps her ---------------------------------------------------
  ask({
    id: 'helpers',
    area: 'helpers',
    kind: 'repeat',
    gate: 'hasHelper',
    entries: ['hasHelper', 'helpersTable'],
    title: 'People who help',
    prompt: 'Who helps her, and how often?',
  }),

  // ---- Help in the day --------------------------------------------------
  ask({
    id: 'day-start',
    area: 'day',
    kind: 'approxDate',
    entries: ['careStartDate'],
    title: 'When the help started',
    prompt: 'Roughly when did she start needing help? A month and year is fine.',
  }),
  ask({
    id: 'day-bed',
    area: 'day',
    kind: 'helpGrid',
    unit: 'tick',
    gate: 'bedNeedsHelp',
    entries: [
      'bedNeedsHelp',
      'bedDifficultyGettingIn',
      'bedDifficultyGettingOut',
      'bedHelpGettingIn',
      'bedHelpGettingOut',
      'bedPromptGettingUp',
      'bedPromptGoingToBed',
    ],
    rows: [
      {
        label: 'Getting into bed',
        difficulty: 'bedDifficultyGettingIn',
        help: 'bedHelpGettingIn',
        prompt: 'bedPromptGoingToBed',
      },
      {
        label: 'Getting out of bed',
        difficulty: 'bedDifficultyGettingOut',
        help: 'bedHelpGettingOut',
        prompt: 'bedPromptGettingUp',
      },
    ],
    title: 'Getting in and out of bed',
    prompt: 'Which of these does she struggle with or need help with?',
  }),
  ask({
    id: 'day-toilet',
    area: 'day',
    kind: 'helpGrid',
    unit: 'perDay',
    gate: 'toiletNeedsHelp',
    entries: [
      'toiletNeedsHelp',
      'toiletDifficultyFrequency',
      'continenceDifficultyFrequency',
      'toiletHelpFrequency',
      'continenceHelpFrequency',
      'toiletPromptFrequency',
      'continencePromptFrequency',
    ],
    rows: [
      {
        label: 'Using the toilet',
        difficulty: 'toiletDifficultyFrequency',
        help: 'toiletHelpFrequency',
        prompt: 'toiletPromptFrequency',
      },
      {
        label: 'Continence',
        difficulty: 'continenceDifficultyFrequency',
        help: 'continenceHelpFrequency',
        prompt: 'continencePromptFrequency',
      },
    ],
    title: 'The toilet in the day',
    prompt: 'How often each day does she struggle or need help?',
  }),
  ask({
    id: 'day-washing',
    area: 'day',
    kind: 'helpGrid',
    unit: 'perDay',
    gate: 'washingNeedsHelp',
    entries: [
      'washingNeedsHelp',
      'appearanceDifficultyFrequency',
      'bathingDifficultyFrequency',
      'washingDifficultyFrequency',
      'showeringDifficultyFrequency',
      'appearanceHelpFrequency',
      'bathingHelpFrequency',
      'washingHelpFrequency',
      'showeringHelpFrequency',
      'appearancePromptFrequency',
      'washingPromptFrequency',
    ],
    rows: [
      {
        label: 'Washing and drying',
        difficulty: 'washingDifficultyFrequency',
        help: 'washingHelpFrequency',
        prompt: 'washingPromptFrequency',
      },
      {
        label: 'Using the shower',
        difficulty: 'showeringDifficultyFrequency',
        help: 'showeringHelpFrequency',
      },
      {
        label: 'Getting in and out of the bath',
        difficulty: 'bathingDifficultyFrequency',
        help: 'bathingHelpFrequency',
      },
      {
        label: { she: 'Looking after her appearance', you: 'Looking after your appearance' },
        difficulty: 'appearanceDifficultyFrequency',
        help: 'appearanceHelpFrequency',
        prompt: 'appearancePromptFrequency',
      },
    ],
    title: 'Washing and bathing',
    prompt: 'How often each day does she struggle or need help?',
  }),
  ask({
    id: 'day-dressing',
    area: 'day',
    kind: 'helpGrid',
    unit: 'perDay',
    gate: 'dressingNeedsHelp',
    entries: [
      'dressingNeedsHelp',
      'dressingDifficultyFrequency',
      'undressingDifficultyFrequency',
      'choosingClothesDifficultyFrequency',
      'dressingHelpFrequency',
      'undressingHelpFrequency',
      'choosingClothesHelpFrequency',
      'dressingPromptFrequency',
      'changingClothesPromptFrequency',
    ],
    rows: [
      {
        label: 'Putting on clothes and shoes',
        difficulty: 'dressingDifficultyFrequency',
        help: 'dressingHelpFrequency',
        prompt: 'dressingPromptFrequency',
      },
      {
        label: 'Taking off clothes and shoes',
        difficulty: 'undressingDifficultyFrequency',
        help: 'undressingHelpFrequency',
      },
      {
        label: 'Choosing what to wear',
        difficulty: 'choosingClothesDifficultyFrequency',
        help: 'choosingClothesHelpFrequency',
      },
      { label: 'Changing clothes', prompt: 'changingClothesPromptFrequency' },
    ],
    title: 'Getting dressed',
    prompt: 'How often each day does she struggle or need help?',
  }),
  ask({
    id: 'day-moving',
    area: 'day',
    kind: 'helpGrid',
    unit: 'tick',
    gate: 'movingIndoorsNeedsHelp',
    entries: [
      'movingIndoorsNeedsHelp',
      'indoorWalkingDifficulty',
      'indoorStairsDifficulty',
      'chairTransferDifficulty',
      'wheelchairTransferDifficulty',
      'indoorWalkingHelp',
      'indoorStairsHelp',
      'chairTransferHelp',
      'wheelchairTransferHelp',
      'movingIndoorsPrompt',
    ],
    rows: [
      {
        label: 'Walking around indoors',
        difficulty: 'indoorWalkingDifficulty',
        help: 'indoorWalkingHelp',
        prompt: 'movingIndoorsPrompt',
      },
      {
        label: 'Stairs',
        difficulty: 'indoorStairsDifficulty',
        help: 'indoorStairsHelp',
      },
      {
        label: 'Getting in or out of a chair',
        difficulty: 'chairTransferDifficulty',
        help: 'chairTransferHelp',
      },
      {
        label: 'Moving to or from a wheelchair',
        difficulty: 'wheelchairTransferDifficulty',
        help: 'wheelchairTransferHelp',
      },
    ],
    title: 'Moving around indoors',
    prompt: 'Which of these does she struggle with or need help with?',
  }),
  ask({
    id: 'day-falls',
    area: 'day',
    kind: 'falls',
    gate: 'fallsOrStumbles',
    entries: [
      'fallsOrStumbles',
      'falls',
      'fallsClinicReferral',
      'needsHelpAfterFall',
      'gettingUpAfterFall',
      'lastFall',
      'lastStumble',
      'fallsLastMonth',
      'fallsLastYear',
      'stumblesLastMonth',
      'stumblesLastYear',
    ],
    title: 'Falls and stumbles',
    prompt: 'Does she fall or stumble because of her conditions?',
  }),
  ask({
    id: 'day-eating',
    area: 'day',
    kind: 'helpGrid',
    unit: 'perDay',
    gate: 'eatingNeedsHelp',
    entries: [
      'eatingNeedsHelp',
      'eatingDifficultyFrequency',
      'cuttingFoodDifficultyFrequency',
      'eatingHelpFrequency',
      'cuttingFoodHelpFrequency',
      'eatingPromptFrequency',
    ],
    rows: [
      {
        label: 'Eating and drinking',
        difficulty: 'eatingDifficultyFrequency',
        help: 'eatingHelpFrequency',
        prompt: 'eatingPromptFrequency',
      },
      {
        label: 'Cutting up food',
        difficulty: 'cuttingFoodDifficultyFrequency',
        help: 'cuttingFoodHelpFrequency',
      },
    ],
    title: 'Eating and drinking',
    prompt: 'How often each day does she struggle or need help?',
  }),
  ask({
    id: 'day-medicines',
    area: 'day',
    kind: 'helpGrid',
    unit: 'perDay',
    gate: 'medicationNeedsHelp',
    entries: [
      'medicationNeedsHelp',
      'medicationDifficultyFrequency',
      'treatmentDifficultyFrequency',
      'medicationHelpFrequency',
      'treatmentHelpFrequency',
      'medicationPromptFrequency',
      'treatmentPromptFrequency',
    ],
    rows: [
      {
        label: 'Taking medicines',
        difficulty: 'medicationDifficultyFrequency',
        help: 'medicationHelpFrequency',
        prompt: 'medicationPromptFrequency',
      },
      {
        label: 'Treatment or therapy',
        difficulty: 'treatmentDifficultyFrequency',
        help: 'treatmentHelpFrequency',
        prompt: 'treatmentPromptFrequency',
      },
    ],
    title: 'Medicines and treatment',
    prompt: 'How often each day does she struggle or need help?',
  }),
  ask({
    id: 'day-communication',
    area: 'day',
    kind: 'helpGrid',
    unit: 'tick',
    gate: 'communicationNeedsHelp',
    entries: [
      'communicationNeedsHelp',
      'commDifficultyUnderstandingOthers',
      'commDifficultyBeingUnderstood',
      'commDifficultyConcentrating',
      'commDifficultyPhone',
      'commDifficultyPost',
      'commDifficultyAskingForHelp',
      'commHelpUnderstandingOthers',
      'commHelpBeingUnderstood',
      'commHelpConcentrating',
      'commHelpPhone',
      'commHelpPost',
      'commHelpAskingForHelp',
    ],
    rows: [
      {
        label: 'Understanding people she does not know well',
        difficulty: 'commDifficultyUnderstandingOthers',
        help: 'commHelpUnderstandingOthers',
      },
      {
        label: 'Being understood',
        difficulty: 'commDifficultyBeingUnderstood',
        help: 'commHelpBeingUnderstood',
      },
      {
        label: 'Concentrating or remembering',
        difficulty: 'commDifficultyConcentrating',
        help: 'commHelpConcentrating',
      },
      {
        label: 'Using the phone',
        difficulty: 'commDifficultyPhone',
        help: 'commHelpPhone',
      },
      {
        label: 'Letters and forms',
        difficulty: 'commDifficultyPost',
        help: 'commHelpPost',
      },
      {
        label: 'Asking for help',
        difficulty: 'commDifficultyAskingForHelp',
        help: 'commHelpAskingForHelp',
      },
    ],
    title: 'Communicating',
    prompt: 'Which of these does she struggle with or need help with?',
  }),
  ask({
    id: 'day-activities',
    area: 'day',
    kind: 'activities',
    gate: 'activityNeedsHelp',
    entries: ['activityNeedsHelp', 'homeActivities', 'outsideActivities'],
    title: 'Hobbies and getting out',
    prompt:
      'Is there anything she does, or would like to do, that she needs someone with her for? Church, a club, seeing friends, the garden.',
  }),
  ask({
    id: 'day-days',
    area: 'day',
    kind: 'amount',
    entries: ['dayCareDaysPerWeek'],
    title: 'Days a week',
    prompt: 'On how many days a week does she need this help in the day?',
  }),
  ask({
    id: 'day-variability',
    area: 'day',
    kind: 'variability',
    entries: ['variabilityDay', 'variabilityDayOften'],
    title: 'Good and bad days',
    prompt: 'Is it worse on some days?',
  }),

  // ---- Keeping her safe in the day -----------------------------------------
  ask({
    id: 'safe-watch',
    area: 'safe',
    kind: 'watch',
    gate: 'daySupervisionNeeded',
    entries: [
      'daySupervisionNeeded',
      'supervisionReasonDanger',
      'supervisionReasonUnawareOfDanger',
      'supervisionReasonSelfNeglect',
      'supervisionReasonSelfHarm',
      'supervisionReasonWandering',
      'supervisionReasonBehaviour',
      'supervisionReasonFits',
      'supervisionReasonConfusion',
      'supervisionReasonVoices',
      'safeAloneDuration',
    ],
    title: 'Someone keeping an eye on her',
    prompt: 'Does someone need to keep an eye on her during the day to keep her safe?',
  }),
  ask({
    id: 'safe-days',
    area: 'safe',
    kind: 'amount',
    entries: ['daySupervisionDaysPerWeek'],
    title: 'Days a week',
    prompt: 'On how many days a week does someone need to keep an eye on her?',
  }),
  ask({
    id: 'safe-variability',
    area: 'safe',
    kind: 'variability',
    entries: ['variabilitySupervision', 'variabilitySupervisionOften'],
    title: 'Good and bad days',
    prompt: 'Is it worse on some days?',
  }),

  // ---- Help at night ---------------------------------------------------------
  ask({
    id: 'night-help',
    area: 'night',
    kind: 'nightGrid',
    gate: 'nightNeedsHelp',
    entries: [
      'nightNeedsHelp',
      'nightPositionFrequency',
      'nightPositionMinutes',
      'nightComfortFrequency',
      'nightComfortMinutes',
      'nightToiletFrequency',
      'nightToiletMinutes',
      'nightContinenceFrequency',
      'nightContinenceMinutes',
      'nightMedicationFrequency',
      'nightMedicationMinutes',
      'nightTreatmentFrequency',
      'nightTreatmentMinutes',
      'nightToiletPromptFrequency',
      'nightToiletPromptMinutes',
      'nightTreatmentPromptFrequency',
      'nightTreatmentPromptMinutes',
    ],
    nightRows: [
      { label: 'Turning over or changing position', times: 'nightPositionFrequency', minutes: 'nightPositionMinutes' },
      { label: 'Getting comfortable', times: 'nightComfortFrequency', minutes: 'nightComfortMinutes' },
      { label: 'The toilet', times: 'nightToiletFrequency', minutes: 'nightToiletMinutes' },
      { label: 'Continence', times: 'nightContinenceFrequency', minutes: 'nightContinenceMinutes' },
      { label: 'Medicines', times: 'nightMedicationFrequency', minutes: 'nightMedicationMinutes' },
      { label: 'Treatment or therapy', times: 'nightTreatmentFrequency', minutes: 'nightTreatmentMinutes' },
      { label: 'Reminding about the toilet', times: 'nightToiletPromptFrequency', minutes: 'nightToiletPromptMinutes' },
      { label: 'Reminding about medicines or treatment', times: 'nightTreatmentPromptFrequency', minutes: 'nightTreatmentPromptMinutes' },
    ],
    title: 'Help during the night',
    prompt: 'What does she need help with at night, how often, and for how long each time?',
  }),
  ask({
    id: 'night-nights',
    area: 'night',
    kind: 'amount',
    entries: ['nightCareNightsPerWeek'],
    title: 'Nights a week',
    prompt: 'On how many nights a week does she need help?',
  }),
  ask({
    id: 'night-watch',
    area: 'night',
    kind: 'watch',
    gate: 'nightSupervisionNeeded',
    entries: [
      'nightSupervisionNeeded',
      'nightWatchReasonDanger',
      'nightWatchReasonUnawareOfDanger',
      'nightWatchReasonSelfHarm',
      'nightWatchReasonWandering',
      'nightWatchReasonBehaviour',
      'nightWatchReasonConfusion',
      'nightWatchReasonVoices',
      'nightSupervisionTimes',
      'nightSupervisionDuration',
    ],
    title: 'Someone awake to watch over her',
    prompt: 'Does someone need to stay awake to watch over her at night?',
  }),
  ask({
    id: 'night-watch-nights',
    area: 'night',
    kind: 'amount',
    entries: ['nightSupervisionNightsPerWeek'],
    title: 'Nights a week',
    prompt: 'On how many nights a week does someone need to watch over her?',
  }),
  ask({
    id: 'night-variability',
    area: 'night',
    kind: 'variability',
    entries: ['variabilityNight', 'variabilityNightOften'],
    title: 'Good and bad nights',
    prompt: 'Is it worse on some nights?',
  }),

  // ---- Hospital and care stays -------------------------------------------
  ask({
    id: 'stays-now',
    area: 'stays',
    kind: 'stay',
    gate: 'currentStay',
    entries: [
      'currentStay',
      'currentStayPlaceType',
      'currentStayFrom',
      'currentStayName',
      'currentStayAddress',
      'currentStayTown',
      'currentStayPostcode',
      'currentStayReason',
      'publiclyFundedStay',
      'fundingAuthority',
    ],
    title: 'Staying somewhere now',
    prompt: 'Is she in a hospital, hospice or care home at the moment?',
  }),
  ask({
    id: 'stays-recent',
    area: 'stays',
    kind: 'stay',
    gate: 'previousStayLastSixWeeks',
    entries: [
      'previousStayLastSixWeeks',
      'previousStayPlaceType',
      'previousStayFrom',
      'previousStayTo',
      'previousStayName',
      'previousStayAddress',
      'previousStayTown',
      'previousStayPostcode',
      'previousStayReason',
    ],
    title: 'A recent stay',
    prompt: 'Has she come out of a hospital, hospice or care home in the last 6 weeks?',
  }),

  // ---- Benefits and payment -----------------------------------------------
  ask({
    id: 'payment-caa',
    area: 'payment',
    kind: 'choice',
    entries: ['constantAttendance'],
    title: 'Constant Attendance Allowance',
    prompt:
      'Does she get Constant Attendance Allowance with a War Pension or Industrial Injuries benefit?',
  }),
  ask({
    id: 'payment-bank',
    area: 'payment',
    kind: 'bank',
    entries: [
      'bankAccountHolder',
      'bankName',
      'bankSortCode',
      'bankAccountNumber',
      'bankAccountHasRollNumber',
      'bankRollNumber',
      'otherBenefitsToAccount',
    ],
    title: 'Where the money goes',
    prompt: 'Which account should Attendance Allowance be paid into?',
  }),

  // ---- Signing and sending ---------------------------------------------------
  ask({
    id: 'signing-signer',
    area: 'signing',
    kind: 'signer',
    entries: [
      'signingForSomeoneElse',
      'signerCategory',
      'poaOnlineAccount',
      'representativeOrganisation',
      'representativeName',
      'representativeNiNumber',
      'representativeDob',
      'representativeAddress',
      'representativePostcode',
      'representativePhone',
    ],
    title: 'Who signs the form',
    prompt: 'Who will sign the form?',
  }),
  ask({
    id: 'signing-sr1',
    area: 'signing',
    kind: 'sr1',
    entries: ['sr1Asked'],
    title: 'Ask for an SR1',
    prompt:
      'Her doctor, nurse or consultant can fill in a short form called an SR1. You can send the claim before it arrives.',
  }),
  ask({
    id: 'signing-consent',
    area: 'signing',
    kind: 'consent',
    entries: ['dwpMedicalContactConsent'],
    title: 'DWP contacting her doctors',
    prompt:
      'DWP may want to contact the people who know about her conditions, such as her GP. Saying no can slow the decision.',
  }),
  ask({
    id: 'signing-name',
    area: 'signing',
    kind: 'confirm',
    entries: ['declarationPrintedName'],
    title: 'Name under the declaration',
    prompt: 'The person signing prints their name under the declaration.',
  }),
  ask({
    id: 'signing-documents',
    area: 'signing',
    kind: 'documents',
    entries: ['attachments'],
    title: 'What to send with it',
    prompt: 'Which documents will you send with the form?',
  }),

  // ---- Make it stronger -------------------------------------------------------
  ask({
    id: 'stronger-words',
    area: 'stronger',
    kind: 'narrative',
    entries: [
      'bedTellUsMore',
      'bedHelp',
      'toiletTellUsMore',
      'toilet',
      'washingTellUsMore',
      'washing',
      'dressingTellUsMore',
      'dressing',
      'movingIndoorsTellUsMore',
      'movingIndoors',
      'eatingTellUsMore',
      'eating',
      'medicationTellUsMore',
      'medicationHelp',
      'communicationTellUsMore',
      'communication',
      'supervisionDayTellUsMore',
      'supervisionDay',
      'nightHelpTellUsMore',
      'nightHelp',
      'nightSupervisionTellUsMore',
      'nightSupervision',
      'variability',
    ],
    title: 'The words on the form',
    prompt: 'We drafted these from what you told us. Change anything that is not quite right.',
  }),
  ask({
    id: 'stronger-supporting',
    area: 'stronger',
    kind: 'supporting',
    entries: [
      'supportingStatementWanted',
      'supporterFrequency',
      'supporterStatement',
      'supporterRelationship',
      'supporterName',
      'supporterAddress',
      'supporterPostcode',
      'supporterPhone',
    ],
    title: 'A few lines from someone who knows her',
    prompt:
      'Someone who knows her well, like a friend, neighbour or her GP, can add a short statement. It cannot be the person signing.',
  }),
  ask({
    id: 'stronger-extra',
    area: 'stronger',
    kind: 'narrative',
    entries: ['extraInformation'],
    title: 'Anything else',
    prompt: 'Anything else DWP should know?',
  }),
];

export const ASK: Record<string, Ask> = Object.fromEntries(
  ASKS.map((a) => [a.id, a]),
);

/** The ask that owns each entry. */
export const OWNER: Record<string, Ask> = Object.fromEntries(
  ASKS.flatMap((a) => a.entries.map((id) => [id, a])),
);

// ---- Ways in: inputs that answer across asks ----------------------------

/**
 * The gate question of each care activity, in the order the batch check lists
 * them. Unticked means "manages this on her own" for the Yes/No gates, and
 * "No" for falls.
 */
export const BATCH_GATES: { id: string; label: Copy }[] = [
  { id: 'bedNeedsHelp', label: 'Getting in and out of bed' },
  { id: 'toiletNeedsHelp', label: 'The toilet' },
  { id: 'washingNeedsHelp', label: 'Washing and bathing' },
  { id: 'dressingNeedsHelp', label: 'Getting dressed' },
  { id: 'movingIndoorsNeedsHelp', label: 'Moving around indoors' },
  { id: 'fallsOrStumbles', label: 'Falls or stumbles' },
  { id: 'eatingNeedsHelp', label: 'Eating and drinking' },
  { id: 'medicationNeedsHelp', label: 'Medicines and treatment' },
  { id: 'communicationNeedsHelp', label: 'Communicating' },
  { id: 'activityNeedsHelp', label: 'Hobbies and getting out' },
  { id: 'daySupervisionNeeded', label: 'Being kept an eye on in the day' },
  { id: 'nightNeedsHelp', label: 'Help at night' },
  { id: 'nightSupervisionNeeded', label: 'Someone awake at night' },
];

export type VoicePrompt = { id: string; area: AreaId; prompt: Copy };

/** The first round, then a second pass per area that is still mostly open. */
export const OPENING_VOICE: VoicePrompt = {
  id: 'voice-opening',
  area: 'day',
  prompt: {
    she: 'Tell me about a typical day and night for {name}: what she struggles with and what help she needs.',
    you: 'Tell me about a typical day and night for you: what you struggle with and what help you need.',
  },
};

export const VOICE_PROMPTS: VoicePrompt[] = [
  { id: 'voice-day', area: 'day', prompt: 'Tell me more about her day: getting up, washing, meals, medicines.' },
  { id: 'voice-safe', area: 'safe', prompt: 'Tell me about keeping her safe: can she be left on her own, and for how long?' },
  { id: 'voice-night', area: 'night', prompt: 'Tell me about her nights: what wakes her, and what help she needs.' },
  { id: 'voice-home', area: 'home', prompt: 'Tell me about her home: the stairs, the bathroom, anything that helps her.' },
  { id: 'voice-health', area: 'health', prompt: 'Tell me about her health: her conditions and who she sees about them.' },
];

/** A voice prompt is offered for an area with at least this many open entries. */
export const VOICE_THRESHOLD = 3;

export type DocumentKind = Extract<
  SourceId,
  'prescription' | 'hospital_letter' | 'care_plan'
>;

export const DOCUMENTS: { kind: DocumentKind; label: string; prompt: string }[] = [
  {
    kind: 'prescription',
    label: 'Photo of her prescription',
    prompt: 'The repeat slip or the list from her pharmacy fills in her medicines and GP.',
  },
  {
    kind: 'hospital_letter',
    label: 'A hospital or clinic letter',
    prompt: 'Appointment or discharge letters fill in who she sees and any stays.',
  },
  {
    kind: 'care_plan',
    label: 'Her care plan',
    prompt: 'A care plan from her carers covers much of her day.',
  },
];

/** Who she is to the form: claiming for her, or she claims herself. */
export type Voice = { name: string; selfClaim: boolean };

/**
 * Third person to second person, for copy written about her. Ordered: the
 * questions and irregular verbs first, then "she <verb>s", then "her", which is
 * "you" after a verb or preposition ("help her", "with her") and "your" before
 * a noun ("her home"). claim.test.ts checks every ask reads cleanly.
 */
const YOU_FORM: [RegExp, string][] = [
  [/\bDoes she\b/g, 'Do you'],
  [/\bdoes she\b/g, 'do you'],
  [/\bIs she\b/g, 'Are you'],
  [/\bis she\b/g, 'are you'],
  [/\bHas she\b/g, 'Have you'],
  [/\bhas she\b/g, 'have you'],
  [/\bdid she\b/g, 'did you'],
  [/\bcan she\b/g, 'can you'],
  [/\bshe is\b/g, 'you are'],
  [/\bshe has\b/g, 'you have'],
  [/\bshe does\b/g, 'you do'],
  [/\bshe was\b/g, 'you were'],
  [/\bshe (\w+?)s\b/g, 'you $1'],
  [/\bshe\b/g, 'you'],
  [/\bShe\b/g, 'You'],
  [/\bher(?=[,.?!]|$| (?:at|during|for|well|and|to|with|safe|up|on|in|from|is)\b)/g, 'you'],
  [/\bher\b/g, 'your'],
  [/\bHer\b/g, 'Your'],
];

const youForm = (text: string) =>
  YOU_FORM.reduce((out, [re, to]) => out.replace(re, to), text);

/** Resolve a piece of copy for the person using the app. */
export const say = (copy: Copy, v: Voice): string => {
  const chosen =
    typeof copy === 'string'
      ? v.selfClaim
        ? youForm(copy)
        : copy
      : v.selfClaim
        ? copy.you
        : copy.she;
  return chosen.replace(/\{name\}/g, v.name);
};
